import React from 'react';
import { ChevronLeft } from 'lucide-react';

interface AppTopBarProps {
  breadcrumb: string[];
  rightActions?: React.ReactNode;
  /** When provided, a Back button is shown before the breadcrumb and invokes this on click. */
  onBack?: (() => void) | null;
}

export function AppTopBar({ breadcrumb, rightActions, onBack }: AppTopBarProps): React.JSX.Element {
  return (
    <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3 px-4 py-3 md:px-6">
        <div className="flex min-w-0 items-center gap-1.5">
          {onBack && (
            <button
              type="button"
              aria-label="Go back"
              onClick={onBack}
              className="-ml-2 inline-flex h-9 shrink-0 items-center gap-1 rounded-full px-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ChevronLeft size={16} />
              <span className="hidden md:inline">Back</span>
            </button>
          )}

          {breadcrumb.length > 0 && (
            <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm">
              {breadcrumb.map((segment, i) => {
                const isLast = i === breadcrumb.length - 1;
                return (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <span className="shrink-0 text-muted-foreground/60" aria-hidden="true">›</span>
                    )}
                    <span
                      className={isLast ? 'truncate font-semibold text-foreground' : 'hidden truncate text-muted-foreground md:block'}
                      {...(isLast ? { 'aria-current': 'page' } : {})}
                    >
                      {segment}
                    </span>
                  </React.Fragment>
                );
              })}
            </nav>
          )}
        </div>

        {rightActions && (
          <div className="flex shrink-0 items-center gap-1">
            {rightActions}
          </div>
        )}
      </div>
    </header>
  );
}
